/**
 * Shared helpers for mutation error handling.
 */

export interface MutationError {
  message: string;
  code?: string;
}

/**
 * Standard shape every InterviewGuides mutation envelope returns.
 *
 * `TPayload` is the entity wrapper (e.g. `{ interviewGuide: {...} }` or
 * `{ scorecard: {...} }`). Application-level errors are surfaced via the
 * `errors` array — these are distinct from network / GraphQL transport
 * errors which bubble up through Apollo's `error` field.
 */
export interface MutationResult<TPayload extends object> {
  errors?: MutationError[] | null;
  // The payload fields (e.g. `interviewGuide`, `scorecard`, `bankQuestion`)
  // are intentionally permissive here — consumers narrow to the concrete
  // wrapper via the `getResult` callback.
}

export class GuideMutationError extends Error {
  public readonly errors: MutationError[];
  constructor(operationName: string, errors: MutationError[]) {
    const messages = errors.map((e) => e.message).join("; ");
    super(`${operationName} failed: ${messages}`);
    this.name = "GuideMutationError";
    this.errors = errors;
  }
}

/**
 * Executes a mutation and checks the result for application-level errors.
 * Throws `GuideMutationError` when the response contains a non-empty `errors` array.
 * Re-throws network/GraphQL transport errors as-is.
 *
 * @example
 * const result = await executeMutation(
 *   "publishGuide",
 *   () => publishGuide({ variables: { id } }),
 *   (d) => d.interviewGuidePublish,
 * );
 */
export async function executeMutation<TData, TPayload extends MutationResult<TPayload> | null | undefined>(
  operationName: string,
  mutationFn: () => Promise<{ data?: TData | null }>,
  getResult: (data: TData) => TPayload,
): Promise<TPayload> {
  let response: { data?: TData | null };
  try {
    response = await mutationFn();
  } catch (error) {
    // Network or GraphQL transport error
    console.error(`[InterviewGuides] ${operationName} network error:`, error);
    throw error;
  }

  const result = response.data ? getResult(response.data) : (null as unknown as TPayload);

  if (result && (result as MutationResult<object>).errors?.length) {
    const err = new GuideMutationError(
      operationName,
      (result as MutationResult<object>).errors as MutationError[],
    );
    console.error(`[InterviewGuides] ${err.message}`);
    throw err;
  }

  return result;
}
